import { memo, useMemo, useCallback } from "react";
import { useSettingsContext } from "@/contexts/SettingsContext";
import { Link } from "react-router-dom";

import styles from "./Footer.module.css";
import FooterLogo from './FooterLogo';

const CURRENT_YEAR = new Date().getFullYear();

const Footer = memo(function Footer() {
	const { settings } = useSettingsContext();

	const footerMenuItems = useMemo(
		() => settings?.footerMenu.map((item) => ({
			id: item.id,
			title: item.menuName,
			href: item.menuLink,
		})) ?? [],
		[settings?.footerMenu]
	);

	const privacyMenuItems = useMemo(
		() => settings?.privacyMenu.map((item) => ({
			id: item.id,
			title: item.menuName,
			href: item.menuLink,
		})) ?? [],
		[settings?.privacyMenu]
	);

	const doulaMenuItems = useMemo(
		() => settings?.doulaMenu.map((item) => ({
			id: item.id,
			title: item.menuName,
			href: item.menuLink,
		})) ?? [],
		[settings?.doulaMenu]
	);

	const websiteDescription = settings?.websiteDescription;

	const handleSmoothScroll = useCallback((href: string) => {
		document.getElementById(href.replace("#", ""))?.scrollIntoView({ behavior: "smooth" });
	}, []);

	return (
		<footer className={`${styles.footer} bg-newTheme-spruce py-12`}>
			<div className={`${styles.footerContainer} px-4 sm:px-6 xl:px-0`}>
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
					{/* Brand */}
					<div className="flex flex-col items-center lg:items-start space-y-4">
						<div className="flex items-center space-x-2">
							<div className={styles.footerLogo}>
								<FooterLogo />
							</div>
						</div>
						{websiteDescription && (
							<p className="font-karla text-center lg:text-left">
								{websiteDescription}
							</p>
						)}
					</div>

					{/* Quick Links */}
					<div className="grid grid-cols-2 gap-4 text-center lg:text-left">
						{footerMenuItems.length > 0 && (
							<div className="space-y-3">
								<h3 className="font-semibold text-white font-karla uppercase">
									Get Started
								</h3>
								<ul className="space-y-2">
									{footerMenuItems.map((item) => (
										<li key={item.id}>
											<Link
												to={item.href}
												className="text-white font-karla transition-colors"
											>
												{item.title}
											</Link>
										</li>
									))}
								</ul>
							</div>
						)}

						{doulaMenuItems.length > 0 && (
							<div className="space-y-3">
								<h3 className="font-semibold text-white font-karla uppercase">
									Learn More
								</h3>
								<ul className="space-y-2">
									{doulaMenuItems.map((item) => (
										<li key={item.id}>
											<Link
												to={item.href}
												className="text-white font-karla transition-colors"
												onClick={() => handleSmoothScroll(item.href)}
											>
												{item.title}
											</Link>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>

					{/* Copyright and Legal Links */}
					<div className="text-center self-center lg:text-right">
						<p className="font-karla">&copy; {CURRENT_YEAR} Gaia</p>
						<p className="font-karla text-sm mt-1">
							All rights reserved.
						</p>
						{privacyMenuItems.length > 0 && (
							<ul className="flex flex-col lg:flex-row lg:justify-end items-center gap-4 mt-4">
								{privacyMenuItems.map((item) => (
									<li key={item.id}>
										<Link
											to={item.href}
											className="font-karla text-sm transition-colors underline"
										>
											{item.title}
										</Link>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			</div>
		</footer>
	);
});

export default Footer;